import { sendToGenericEscposPrinter, getGenericEscposPrintersConfig } from '@/services/printers/genericEscpos';
import { sendToUsbAndroidPrinter, getUsbAndroidPrintersConfig } from '@/services/printers/usbAndroid';
import { sendToUsbWindowsPrinter, getUsbWindowsPrintersConfig } from '@/services/printers/usbWindows';
import { isBackendAvailableOnDevice } from '@/services/printers/printerRouting';
import type { PrinterBackend, PrinterDuty, PrinterTemplate } from '@/services/printers/types';

interface DispatchCopyPrinter {
  duties: PrinterDuty[];
  printDispatchCopy?: boolean;
}

interface DispatchCopyTarget {
  backend: PrinterBackend;
  printerId: string;
}

export const printerPrintsDispatchCopy = (printer: DispatchCopyPrinter): boolean =>
  printer.duties.includes('cashier') && printer.printDispatchCopy === true;

const collectDispatchCopyTargets = (): DispatchCopyTarget[] => {
  const targets: DispatchCopyTarget[] = [];

  if (isBackendAvailableOnDevice('usb_windows')) {
    const config = getUsbWindowsPrintersConfig();
    if (config.enabled) {
      for (const printer of config.printers) {
        if (printerPrintsDispatchCopy(printer)) {
          targets.push({ backend: 'usb_windows', printerId: printer.id });
        }
      }
    }
  }

  if (isBackendAvailableOnDevice('usb_android')) {
    const config = getUsbAndroidPrintersConfig();
    if (config.enabled) {
      for (const printer of config.printers) {
        if (printerPrintsDispatchCopy(printer)) {
          targets.push({ backend: 'usb_android', printerId: printer.id });
        }
      }
    }
  }

  if (isBackendAvailableOnDevice('generic_escpos')) {
    const config = getGenericEscposPrintersConfig();
    if (config.enabled) {
      for (const printer of config.printers) {
        if (printerPrintsDispatchCopy(printer)) {
          targets.push({ backend: 'generic_escpos', printerId: printer.id });
        }
      }
    }
  }

  return targets;
};

const sendToTarget = (template: PrinterTemplate, target: DispatchCopyTarget): Promise<boolean> => {
  const options = { openCashDrawer: false };
  if (target.backend === 'usb_windows') {
    return sendToUsbWindowsPrinter(template, target.printerId, options);
  }
  if (target.backend === 'usb_android') {
    return sendToUsbAndroidPrinter(template, target.printerId, options);
  }
  return sendToGenericEscposPrinter(template, target.printerId, options);
};

/** Sends one template only to cashier printers that opted into the dispatch copy. */
export const printCashierDispatchCopy = async (template: PrinterTemplate): Promise<boolean> => {
  const targets = collectDispatchCopyTargets();
  if (targets.length === 0) return false;

  const results = await Promise.all(targets.map((target) => sendToTarget(template, target)));
  return results.some((success) => success);
};
